import { useState } from "react"
import styled from "styled-components"
import { Upload, Trash2, X, Edit2, Star  } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import toast from "react-hot-toast"
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const Input = styled.input`
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #4A4B4F;
  background-color: #3A3B3E;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4A88F3;
  }
`
const ImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 60px;
  gap: 20px;
`

const ImageContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 400px;
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
`

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`
const ImageButton = styled.button`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  padding: 5px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: white;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`

const DeleteImageButton = styled(ImageButton)`
  top: 5px;
  right: 5px;
`

const EditImageButton = styled(ImageButton)`
  top: 5px;
  right: 33px;
`

const PrimaryImageStar = styled(Star)`
  position: absolute;
  top: 5px;
  left: 5px;
  color: gold;
  filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.5));
`

const Button = styled.button`
  padding: 12px 20px;
  border-radius: 4px;
  border: none;
  background-color: #4A88F3;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #3B78E0;
  }

  &:disabled {
    background-color: #4A4B4F;
    cursor: not-allowed;
  }
`

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`
const ColorInput = styled(Input)`
  height: 40px;
  padding: 5px;
`

const ModalContent = styled(motion.div)`
  background-color: #2A2B2E;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const ModalTitle = styled.h2`
  color: #FFFFFF;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #FFFFFF;
  cursor: pointer;
  font-size: 1.5rem;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  color: #FFFFFF;
  font-size: 0.9rem;
`



const Checkbox = styled.input`
  margin-right: 10px;
`



export const ProductImages = ({ images, onAddImage, onDeleteImage, onEditImage, create }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteIndex, setDeleteIndex] = useState(null)
  const [editingImage, setEditingImage] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [imageable, setImageable] = useState(false)
  const [newImage, setNewImage] = useState({
    url: "",
    isPrimary: false,
    color: "#000000",
  })

  
  const handleAddImage = (e) => {
    e.preventDefault()
    if (create) {
      return toast("Debe crearse el producto antes de añadir imágenes", {
        icon: <WarningAmberIcon />
      })
    }
    if (newImage.isPrimary === true && images.some((image) => image.isPrimary)) {
      return toast("Ya hay una imagen primaria", {
        icon: <WarningAmberIcon />
      })
    }
    onAddImage(newImage)
    setIsAddModalOpen(false)
    setNewImage({ url: "", isPrimary: false, color: "#000000" })
  }

  const handleDeleteImage = (image) => {
    setDeleteIndex(image)
    setIsDeleteModalOpen(true)
  }

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      onDeleteImage(deleteIndex)
      setIsDeleteModalOpen(false)
      setDeleteIndex(null)
    }
  }

  const handleEditImage = (image, index) => {
    setEditingImage({ ...image, index })
    if (image.color == "" || null) setImageable(true)
    else setImageable(false)
    setIsEditModalOpen(true)
  }
  const handleSaveEdit = (e) => {
    e.preventDefault()
    if (editingImage && typeof editingImage.index === "number") {
      if (editingImage.isPrimary === true && images.some((image) => image.isPrimary) && !images[editingImage.index].isPrimary) {
        return toast("Ya hay una imagen primaria", {
          icon: <WarningAmberIcon />
        })
      } 
      if (imageable){
        const editImage = { ...editingImage, color: "" }
        onEditImage(editingImage.index, editImage)
        setIsEditModalOpen(false)
        setEditingImage(null)
      }
      else{
        onEditImage(editingImage.index, editingImage)
        setIsEditModalOpen(false)
        setEditingImage(null)
      }

    }
  }

  return (
    <ImageSection>
      <h2>Imágenes del Producto</h2>
      <ImageContainer>
        {images?.map((image, index) => (
          <ImageWrapper key={index}>
            <Image src={image?.url?.secure_url || "/placeholder.svg"} alt={`Producto ${index + 1}`} />
            {image?.isPrimary && <PrimaryImageStar size={16} />}
            <EditImageButton onClick={() => handleEditImage(image, index)} aria-label="Editar imagen">
              <Edit2 size={14} />
            </EditImageButton>
            <DeleteImageButton onClick={() => handleDeleteImage(image)} aria-label="Eliminar imagen">
              <Trash2 size={14} />
            </DeleteImageButton>
          </ImageWrapper>
        ))}
      </ImageContainer>
      {/* {images.length <= 5 && */}
          <Button onClick={() => { if (create) {
            return toast("Debe crearse el producto antes de añadir imágenes", {
              icon: <WarningAmberIcon />
            })
            }; 
            setIsAddModalOpen(true)
            }}>
          <Upload size={16} style={{ marginRight: "8px" }} />
          Añadir Imagen
        </Button>
  
      {/* } */}
      <AnimatePresence>
        {isAddModalOpen && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <ModalTitle>Añadir Imagen</ModalTitle>
                <CloseButton onClick={() => setIsAddModalOpen(false)}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>
              <Form onSubmit={handleAddImage}>
                <FormGroup>
                  <Label htmlFor="imageUrl">URL de la imagen</Label>
                  <Input
                    id="imageUrl"
                    type="file"
                    // value={newImage.url}
                    onChange={(e) => setNewImage({ ...newImage, url: e.target.files[0] })}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>
                    <Checkbox
                      type="checkbox"
                      checked={newImage.isPrimary}
                      onChange={(e) => setNewImage({ ...newImage, isPrimary: e.target.checked })}
                    />
                    Imagen primaria
                  </Label>
                </FormGroup>
                <FormGroup>
                  <Label>
                    <Checkbox
                      type="checkbox"
                      checked={imageable}
                      onChange={(e) => setImageable(e.target.checked)}
                    />
                    Imagen sin color
                  </Label>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="imageColor">Color</Label>
                  <ColorInput
                    id="imageColor"
                    type="color"
                    value={newImage.color}
                    onChange={(e) => setNewImage({ ...newImage, color: e.target.value })}
                  />
                </FormGroup>
                <Button type="submit">Añadir Imagen</Button>
              </Form>
            </ModalContent>
          </ModalOverlay>
        )}

        {isDeleteModalOpen && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <ModalTitle>Confirmar Eliminación</ModalTitle>
                <CloseButton onClick={() => setIsDeleteModalOpen(false)}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>
              <p style={{ color: "#FFFFFF", marginBottom: "20px" }}>
                ¿Estás seguro de que quieres eliminar esta imagen?
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <Button onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
                <Button onClick={confirmDelete} style={{ backgroundColor: "#FF4444" }}>
                  Eliminar
                </Button>
              </div>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
      {isEditModalOpen && editingImage && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <ModalHeader>
                <ModalTitle>Editar Imagen</ModalTitle>
                <CloseButton onClick={() => setIsEditModalOpen(false)}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>
              <Form onSubmit={handleSaveEdit}>
                <FormGroup>
                  <Label htmlFor="editImageUrl">URL de la imagen</Label>
                  <Input
                    id="editImageUrl"
                    type="file"
                    // value={editingImage.url}
                    onChange={(e) => setEditingImage({ ...editingImage, url: e.target.files[0] })}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>
                    <Checkbox
                      type="checkbox"
                      checked={editingImage.isPrimary}
                      onChange={(e) => setEditingImage({ ...editingImage, isPrimary: e.target.checked })}
                    />
                    Imagen primaria
                  </Label>
                </FormGroup>
                <FormGroup>
                  <Label>
                    <Checkbox
                      type="checkbox"
                      checked={imageable}
                      onChange={(e) => setImageable(e.target.checked)}
                    />
                    Imagen sin color
                  </Label>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="editImageColor">Color</Label>
                  <ColorInput
                    id="editImageColor"
                    type="color"
                    value={editingImage.color}
                    onChange={(e) => setEditingImage({ ...editingImage, color: e.target.value })}
                  />
                </FormGroup>
                <Button type="submit">Guardar Cambios</Button>
              </Form>
            </ModalContent>
          </ModalOverlay>
        )}

    </ImageSection>
  )
}

